
import { useState, useEffect } from 'react';
import { isInWishlist, toggleWishlist } from '../../utils/wishlist';

interface WishlistButtonProps {
  product: {
    id: number;
    name: string;
    price: number;
    image: string;
    category: string;
  };
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showLabel?: boolean;
}

/**
 * WishlistButton – a reusable button that toggles a product in the wishlist.
 *
 * Fixed Issues:
 * 1. Tailwind `group/wish` variant caused a syntax error (`Expected identifier but found "/"`).
 *    Replaced it with the standard `group` variant and added a custom `wish-group` class
 *    for potential future styling without breaking the parser.
 * 2. Added defensive error handling around `toggleWishlist` to avoid unhandled exceptions.
 * 3. Ensured the component remains robust by guarding against missing product data.
 * 4. Moved hooks before early return to comply with React's Rules of Hooks.
 */
export default function WishlistButton({
  product,
  size = 'md',
  className = '',
  showLabel = false,
}: WishlistButtonProps) {
  const [liked, setLiked] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    // Guard against invalid product inside the effect
    if (!product || typeof product.id !== 'number') {
      return;
    }

    // Initialise liked state
    setLiked(isInWishlist(product.id));

    // Listen for external wishlist updates
    const handleUpdate = () => setLiked(isInWishlist(product.id));
    window.addEventListener('wishlistUpdated', handleUpdate);
    return () => window.removeEventListener('wishlistUpdated', handleUpdate);
  }, [product?.id]);

  // Guard against undefined product (should never happen, but protects against runtime crashes)
  if (!product || typeof product.id !== 'number') {
    console.error('WishlistButton received an invalid product prop:', product);
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Trigger animation
    setAnimating(true);
    setTimeout(() => setAnimating(false), 400);

    // Toggle wishlist with error handling
    try {
      toggleWishlist(product);
    } catch (err) {
      console.error('Failed to toggle wishlist for product', product.id, err);
    }
  };

  const sizeClasses: Record<'sm' | 'md' | 'lg', string> = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-lg',
    lg: 'w-12 h-12 text-xl',
  };

  return (
    <button
      onClick={handleClick}
      className={`
        group wish-group flex items-center justify-center rounded-full
        transition-all duration-300 cursor-pointer whitespace-nowrap
        ${liked ? 'bg-red-50 text-red-500 hover:bg-red-100' : 'bg-white/90 text-gray-400 hover:text-red-500 hover:bg-white'}
        ${sizeClasses[size]} ${className}
      `}
      aria-label={liked ? 'Remove from wishlist' : 'Add to wishlist'}
      title={liked ? 'Remove from wishlist' : 'Add to wishlist'}
    >
      <i
        className={`
          ${liked ? 'ri-heart-3-fill' : 'ri-heart-3-line'}
          transition-transform duration-300
          ${animating ? 'scale-125' : 'scale-100'}
        `}
      />
      {showLabel && (
        <span className="ml-2 text-sm font-medium">
          {liked ? 'Saved' : 'Save'}
        </span>
      )}
    </button>
  );
}
